import { useVirtualKeyboard } from 'hooks/useVirtualKeyboard';
import { get } from 'lodash';
import { ComponentProps, Key, useRef } from 'react';
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { Controller, useFormContext } from 'react-hook-form';
import { InputErrorMessage } from './InputErrorMessage';

interface Props<T extends object> extends Partial<ComponentProps<typeof Typeahead>> {
    name: string;
    label?: string;
    rules?: { required?: boolean };
    data: T[];
    transformSelected?: (selected: any) => any;
    dataKey: any;
    className?: string;
    minimizedDataKey?: Key; // transforms the selected array to only this key and back

    [x: string]: any; // ! get types from Typeahead
}

export const AutoCompleteTextInput = <T extends object>({
    name,
    label,
    rules = {},
    className,
    transformSelected,
    data,
    dataKey,
    minimizedDataKey,
    inputProps,
    ...props
}: Props<T>) => {
    const {
        control,
        formState: { errors }
    } = useFormContext();
    const error = get(errors, name);
    const typeaheadRef = useRef<any>(undefined);
    useVirtualKeyboard(typeaheadRef.current);

    return (
        <div className={'form-group' + (className ? ' ' + className : '')}>
            {label && <label>{label + (rules.required ? ' *' : '')}</label>}
            <Controller
                control={control}
                name={name}
                defaultValue={[]}
                render={({ field: { onChange, onBlur, value, name } }) => (
                    <Typeahead
                        id={name}
                        ref={typeaheadRef}
                        selected={
                            transformSelected?.(value) || minimizedDataKey
                                ? data?.filter((v) => value?.includes(v?.[String(minimizedDataKey)]))
                                : value || []
                        }
                        onChange={(selected) =>
                            onChange(minimizedDataKey ? selected.map((v) => v?.[String(minimizedDataKey)]) : selected)
                        }
                        isInvalid={error}
                        onBlur={() => {
                            onBlur();
                            typeaheadRef.current?.hideMenu();
                        }}
                        inputProps={{ value, name, ...inputProps }}
                        labelKey={dataKey}
                        options={data || []}
                        highlightOnlyResult={!props?.allowNew}
                        clearButton
                        {...props}
                    />
                )}
            />

            <InputErrorMessage errors={errors} name={name} />
        </div>
    );
};
